import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, PenLine, Plus, Rss, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { DAYS, FieldLabel, TagInput } from "@/admin/ui";
import { useAdminCategories, useMeta, useRefresh, useSources } from "@/admin/hooks";

const DEFAULT = { name: "", type: "rss", enabled: true, cadence_type: "interval", every_hours: 6, times: [], days: [0, 1, 2, 3, 4, 5, 6],
  count: 1, category: null, tone: null, publish: true, generate_image: true, topics: [], keywords: [], source_ids: [] };
const HOURS = [1, 2, 3, 4, 6, 8, 12, 24, 48];

const Step = ({ n, title, children }) => (
  <section className="border-t border-border pt-5">
    <p className="flex items-center gap-2 font-semibold mb-3"><span className="h-6 w-6 grid place-items-center bg-foreground text-background text-xs font-mono">{n}</span>{title}</p>
    {children}
  </section>
);

const Choice = ({ active, onClick, icon: Icon, title, text, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} className={`text-left border-2 p-4 transition-colors ${active ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40"}`}>
    <Icon className={`h-5 w-5 ${active ? "text-primary" : "text-muted-foreground"}`} />
    <p className="font-semibold mt-2">{title}</p>
    <p className="text-xs text-muted-foreground mt-0.5">{text}</p>
  </button>
);

const WhatStep = ({ f, set, topicsText, setTopicsText, sources }) => (
  <Step n="1" title="¿Qué debe escribir?">
    <div className="grid sm:grid-cols-2 gap-3">
      <Choice active={f.type === "rss"} onClick={() => set({ type: "rss" })} icon={Rss} title="Replicar noticias" text="Toma los titulares de tus fuentes y los reescribe con tu estilo" testid="auto-type-rss" />
      <Choice active={f.type === "topic"} onClick={() => set({ type: "topic" })} icon={PenLine} title="Contenido propio" text="Artículos de análisis sobre los temas que tú elijas" testid="auto-type-topic" />
    </div>
    {f.type === "rss" ? (
      <div className="mt-4 space-y-4">
        <div>
          <FieldLabel>Fuentes</FieldLabel>
          <label className="flex items-center gap-2 text-sm mb-2"><Checkbox checked={!f.source_ids.length} onCheckedChange={() => set({ source_ids: [] })} data-testid="auto-sources-all" /> Todas las fuentes activas</label>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {sources?.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox checked={f.source_ids.includes(s.id)} data-testid={`auto-source-${s.id}`}
                  onCheckedChange={(v) => set({ source_ids: v ? [...f.source_ids, s.id] : f.source_ids.filter((x) => x !== s.id) })} />
                {s.name}
              </label>
            ))}
          </div>
        </div>
        <div>
          <FieldLabel hint="opcional">Solo noticias que mencionen…</FieldLabel>
          <TagInput value={f.keywords} onChange={(keywords) => set({ keywords })} placeholder="banca, startups, Ibex…" testid="auto-keywords" />
        </div>
      </div>
    ) : (
      <div className="mt-4">
        <FieldLabel hint="uno por línea, se van turnando">Temas</FieldLabel>
        <Textarea rows={5} value={topicsText} onChange={(e) => setTopicsText(e.target.value)} placeholder={"Financiación para pymes\nInteligencia artificial en la empresa"} data-testid="auto-topics-input" />
      </div>
    )}
  </Step>
);

const WhenStep = ({ f, set }) => {
  const [time, setTime] = useState("14:00");
  return (
    <Step n="2" title="¿Cuándo?">
      <div className="grid grid-cols-2 gap-1 p-1 bg-muted mb-4">
        {[["interval", "Cada X horas"], ["daily", "A horas fijas"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => set({ cadence_type: k })} data-testid={`auto-cadence-${k}`} className={`py-2 text-sm ${f.cadence_type === k ? "bg-card shadow-sm font-semibold" : "text-muted-foreground"}`}>{label}</button>
        ))}
      </div>
      {f.cadence_type === "interval" ? (
        <Select value={String(f.every_hours)} onValueChange={(v) => set({ every_hours: Number(v) })}>
          <SelectTrigger data-testid="auto-every-hours-select"><SelectValue /></SelectTrigger>
          <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{h === 1 ? "Cada hora" : `Cada ${h} horas`}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d, i) => (
              <button key={d} type="button" data-testid={`auto-day-${i}`} onClick={() => set({ days: f.days.includes(i) ? f.days.filter((x) => x !== i) : [...f.days, i] })}
                className={`h-9 w-9 text-sm font-semibold border ${f.days.includes(i) ? "bg-foreground text-background border-foreground" : "border-border"}`}>{d}</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!f.times.length && <span className="text-sm text-muted-foreground">Añade al menos una hora →</span>}
            {f.times.map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 bg-secondary px-2.5 py-1.5 font-mono text-sm">{t}
                <button type="button" onClick={() => set({ times: f.times.filter((x) => x !== t) })} aria-label={`Quitar ${t}`}><X className="h-3 w-3" /></button>
              </span>
            ))}
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="border border-input bg-background h-9 px-2 text-sm" data-testid="auto-time-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => time && set({ times: [...new Set([...f.times, time])].sort() })} data-testid="auto-time-add"><Plus /> Añadir hora</Button>
          </div>
          <p className="text-xs text-muted-foreground">Hora peninsular española.</p>
        </div>
      )}
    </Step>
  );
};

const HowStep = ({ f, set, meta, cats }) => (
  <Step n="3" title="¿Cómo publica?">
    <div className="grid sm:grid-cols-3 gap-3">
      <div>
        <FieldLabel>Noticias cada vez</FieldLabel>
        <Select value={String(f.count)} onValueChange={(v) => set({ count: Number(v) })}>
          <SelectTrigger data-testid="auto-count-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <FieldLabel>Sección</FieldLabel>
        <Select value={f.category || "auto"} onValueChange={(v) => set({ category: v === "auto" ? null : v })}>
          <SelectTrigger data-testid="auto-category-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="auto">Automática</SelectItem>{cats?.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <FieldLabel>Tono</FieldLabel>
        <Select value={f.tone || "default"} onValueChange={(v) => set({ tone: v === "default" ? null : v })}>
          <SelectTrigger data-testid="auto-tone-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="default">Por defecto</SelectItem>{meta?.tones.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
    <div className="grid sm:grid-cols-2 gap-2 mt-4">
      {[[true, "Publicar automáticamente", "Aparece en la web al momento"], [false, "Guardar como borrador", "Tú la revisas y publicas"]].map(([v, title, text]) => (
        <button key={title} type="button" onClick={() => set({ publish: v })} data-testid={`auto-publish-${v ? "auto" : "draft"}`}
          className={`text-left border-2 p-3 ${f.publish === v ? "border-primary bg-primary/5" : "border-border"}`}>
          <p className="text-sm font-semibold">{title}</p><p className="text-xs text-muted-foreground">{text}</p>
        </button>
      ))}
    </div>
    <div className="grid sm:grid-cols-2 gap-2 mt-3">
      <label className="flex items-center justify-between border border-border p-3 text-sm">Crear imagen con IA<Switch checked={f.generate_image} onCheckedChange={(v) => set({ generate_image: v })} data-testid="auto-image-switch" /></label>
      <label className="flex items-center justify-between border border-border p-3 text-sm">Activada<Switch checked={f.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="auto-enabled-switch" /></label>
    </div>
  </Step>
);

export const AutomationDialog = ({ open, onOpenChange, automation }) => {
  const [f, setF] = useState(DEFAULT);
  const [topicsText, setTopicsText] = useState("");
  const { data: meta } = useMeta();
  const { data: cats } = useAdminCategories();
  const { data: sources } = useSources();
  const refresh = useRefresh();
  const set = (p) => setF((x) => ({ ...x, ...p }));

  useEffect(() => {
    if (!open) return;
    const base = automation ? { ...DEFAULT, ...automation } : DEFAULT;
    setF(base);
    setTopicsText((base.topics || []).join("\n"));
  }, [open, automation]);

  const save = useMutation({
    mutationFn: () => {
      const body = { ...f, topics: topicsText.split("\n").map((t) => t.trim()).filter(Boolean), name: f.name.trim() || (f.type === "rss" ? "Replicar noticias" : "Contenido propio") };
      return automation ? api.put(`/admin/automations/${automation.id}`, body) : api.post("/admin/automations", body);
    },
    onSuccess: () => { toast.success(automation ? "Automatización guardada" : "Automatización creada"); refresh(); onOpenChange(false); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="automation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{automation ? "Editar automatización" : "Nueva automatización"}</DialogTitle>
          <DialogDescription>Tres pasos: qué escribe, cuándo y cómo se publica.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div><FieldLabel>Nombre</FieldLabel><Input value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="Ej.: Titulares de la mañana" data-testid="auto-name-input" /></div>
          <WhatStep f={f} set={set} topicsText={topicsText} setTopicsText={setTopicsText} sources={sources} />
          <WhenStep f={f} set={set} />
          <HowStep f={f} set={set} meta={meta} cats={cats} />
          <Button className="w-full h-11" onClick={() => save.mutate()} disabled={save.isPending} data-testid="auto-save-btn">
            {save.isPending && <Loader2 className="animate-spin" />} {automation ? "Guardar cambios" : "Crear automatización"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
